"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * A captured email, drawn the way it arrived.
 *
 * The row stores both halves of the mail. `body` is the template's plain-text
 * fallback, which is not prose: preheader padding, the copy, then every footer
 * link the layout carries. Rendering that in a chat bubble is what produced
 * "$5 just landed ... https://terac.com ... Manage email preferences" as one
 * paragraph. `bodyHtml` is the document somebody actually received.
 *
 * ## Why an iframe and not sanitized markup inline
 *
 * The obvious alternative is `sanitizeEmailHtml` plus a `dangerouslySetInnerHTML`
 * in a scoped container, and it is the wrong tool here. That sanitizer exists to
 * make an inbound RFQ readable: it drops `style`, `class` and images on purpose.
 * Run over one of our own React Email templates it returns an unstyled skeleton
 * of nested tables -- every colour, width and font gone -- which is the opposite
 * of showing somebody what we sent. And what survives would still be loose in the
 * dashboard's own cascade.
 *
 * A separate document solves both at once. The mail keeps its 600px table
 * layout, its fonts and its colours; its CSS cannot reach the page and the
 * page's cannot reach it.
 *
 * ## What the sandbox allows, and what it must never allow
 *
 * `allow-scripts` is absent and has to stay absent. Nothing inside can execute,
 * which is what makes the rest safe: `allow-same-origin` grants the *frame* no
 * capability it can act on, and is here only so this component can measure the
 * rendered height from outside. The pair is only dangerous together, because
 * then frame script could reach the parent origin.
 *
 * Also absent: `allow-forms` (a captured mail must not be able to post
 * anywhere), `allow-modals`, and `allow-top-navigation` (it must not be able to
 * move the dashboard off the page). `allow-popups` with
 * `allow-popups-to-escape-sandbox` is present so a link somebody deliberately
 * clicks opens as an ordinary tab rather than replacing the mail inside its own
 * frame.
 *
 * Remote images load. On an inbound mail they were already stripped at write
 * time by `sanitizeEmailHtml`; on our own outbound the "tracker" is our own.
 */
export function EmailBody({
  html,
  ownSide,
}: {
  html: string;
  ownSide: boolean;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(0);

  /**
   * The document's own height, so the mail sits in the thread at its natural
   * size instead of inside a second scroller. Readable because the frame is
   * same-origin; nothing in it can run, so there is nothing on the other side
   * to read back.
   */
  const measure = useCallback(() => {
    const doc = frame.current?.contentDocument;
    if (!doc?.body) {
      return;
    }
    // The tallest of the three, because no one of them is right on its own. A
    // mail laid out in tables can leave `body.scrollHeight` short of what the
    // document actually occupies -- which clipped the call-to-action off the
    // bottom of every template that has one -- and `documentElement` is the
    // element whose height this component is setting, so on its own it would
    // only ever measure itself.
    const measured = Math.max(
      doc.body.scrollHeight,
      doc.body.offsetHeight,
      doc.documentElement.scrollHeight,
    );
    setHeight(Math.min(measured, MAX_HEIGHT));
  }, []);

  // Images and webfonts land after `load`, and each one moves the bottom of the
  // document. Watching the body keeps the frame the size of what is in it, and
  // each image is watched too: a logo settling inside a table cell resizes the
  // cell without the body's own box ever changing, so the observer alone never
  // fired and the mail stayed at whatever height it had before the picture
  // arrived.
  useEffect(() => {
    const doc = frame.current?.contentDocument;
    if (!doc?.body || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(doc.body);
    observer.observe(doc.documentElement);
    const images = [...doc.images];
    for (const image of images) {
      image.addEventListener("load", measure);
    }
    return () => {
      observer.disconnect();
      for (const image of images) {
        image.removeEventListener("load", measure);
      }
    };
  }, [measure, html]);

  return (
    <div
      className={
        // A card and not a bubble: this is a document somebody was sent, and a
        // 600px marketing layout inside a speech balloon reads as neither. The
        // side it sits on still says who sent it.
        // `shrink-0` is load-bearing: the thread is a flex column, so a card
        // taller than the viewport is a flex item the browser is free to
        // compress -- which squashed the mail to the height of the scroller and
        // cut its call-to-action off the bottom, with nothing to scroll to
        // because the scroller thought its content ended there.
        `border-border bg-background mb-4 w-full max-w-[min(42rem,88%)] shrink-0 overflow-hidden rounded-[12px] border ${
          ownSide ? "self-end" : "self-start"
        }`
      }
    >
      <iframe
        ref={frame}
        title="Email message"
        srcDoc={framed(html)}
        onLoad={measure}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        loading="lazy"
        className="block w-full"
        style={{ height: height || 220 }}
      />
    </div>
  );
}

/** Past this a mail is a document to open, not a thing to read in a thread. */
const MAX_HEIGHT = 1400;

const INVISIBLE = new RegExp("[\\u00ad\\u200b-\\u200f\\u2060\\ufeff]", "g");

/**
 * The stored HTML as a standalone document.
 *
 * A React Email render is already a whole `<html>`, and an inbound reply is a
 * fragment the sanitizer left behind. Both have to end up as a document with a
 * `<base target="_blank">`, so a link inside opens in a tab rather than loading
 * the destination inside the frame in place of the mail.
 */
function framed(raw: string): string {
  // The preheader's padding, which a mail client never shows: it is inside a
  // hidden element in a full template, but a capture that lost the styling
  // leaves it as visible text and it renders as a column of blank lines.
  // Twin of `INVISIBLE` in `chat/email-conversations.ts`, which does the same
  // for the list preview; kept here rather than imported so this component
  // pulls nothing server-side into the browser bundle.
  const html = raw.replace(INVISIBLE, "");
  const base = '<base target="_blank">';
  if (/<html[\s>]/i.test(html)) {
    return /<head[^>]*>/i.test(html)
      ? html.replace(/<head[^>]*>/i, (head) => `${head}${base}`)
      : html.replace(/<html[^>]*>/i, (tag) => `${tag}<head>${base}</head>`);
  }
  return `<!doctype html><html><head><meta charset="utf-8">${base}<style>html,body{margin:0;padding:16px;font:14px/1.5 system-ui,sans-serif;overflow-x:hidden}img{max-width:100%;height:auto}</style></head><body>${html}</body></html>`;
}
